import * as angular from 'angular';
import './search.component.scss';
/**
 * @ngdoc directive
 * @name mwSearch
 * @module mw.components.search
 *
 * @description
 * `<mw-search>` is a component used to display a search bar.
 *
 * @param {string} [placeholder=Search] - Text to be displayed when there are no search terms added yet.
 * @param {string} [secondary-placeholder] - Text to be displayed when there are 1 or more terms added.
 * @param {boolean} [show-icon=true] - show/hide lens icon.
 * @param {boolean} [clear-always-visible=false] - show/hide clear button when whe search bar is empty.
 * @param {boolean} [is-focused=false] - Control if the search input is focused or not.
 * @param {array} [separator-keys=[]] - List of separator keys.
 * @param {array} [terms=[]] - Used to override component terms.
 * @param {string} [chip-template] - template string to override the default chip item template.
 * @param {string} [item-template] - template String to ovveride the autocomplete item template.
 * @param {expression} [on-search] - Function that is called when a search term have been input on key up. $event is an array of search term strings.
 * @param {expression} [on-change] - Function that is called when a term is added or removed.  $event is an array of search term strings.
 * @param {expression} [on-add] - Function that is called when a term is added.  $event is an array of search term strings.
 * @param {expression} [on-remove] - Function that is called when a term is removed. $event is an array of search term strings.
 * @param {expression} [on-clear] - Function that is called when all terms are removed.
 * @param {expression} [on-autocomplete-query] - Function that is called when a key is pressed in the text input. $event is the search string.
 *
 * @usage
 * <hljs hljs-language="html">
 *   <mw-search on-change="$ctrl.onChange($event)"></mw-search>
 * </hljs>
 *
 */


const defaultAutoCompleteItemTemplate = `
    <md-item-template>
        <div class="mw-autocomplete--result" flex layout="row" layout-align="start center">
            <mw-icon class="mw-search__autocomplete-icon" svg-icon="action:ic_search_24px"></mw-icon>
            <span md-highlight-text="$ctrl.searchText">{{item}}</span>
        </div>
    </md-item-template>`;

const defaultChipTemplate = `
    <md-chip-template>
        {{$chip}}
        <md-tooltip md-delay="1500">{{$chip}}</md-tooltip>
    </md-chip-template>
`;

const defaultTemplate = `
    <form name="mw-search-form">
        <mw-icon class="mw-search__icon hide-xs" svg-icon="action:ic_search_24px" ng-if="$ctrl.showIcon"></mw-icon>
        <mw-icon-button class="mw-search__delete" svg-icon="navigation:ic_close_24px" ng-show="$ctrl.terms.length > 0 || $ctrl.clearAlwaysVisible" ng-click="$ctrl.deleteAll()"></mw-icon-button>
        <md-chips ng-model="$ctrl.terms" md-removable="$ctrl.chips.isRemovable" readonly="$ctrl.chips.isReadOnly"
            class="md-primary" md-separator-keys="$ctrl.chips.separatorKeys"
            md-on-add="$ctrl.onAddChip()" md-on-remove="$ctrl.onRemoveChip()"
            ng-class="{ 'mw-search__empty': $ctrl.terms.length == 0, 'mw-search__icon-hidden':!$ctrl.showIcon}"
            md-autocomplete-snap="width" md-require-match="false">

            <md-autocomplete
                id="test"
                md-search-text="$ctrl.searchText"
                md-items="item in $ctrl.querySearch($ctrl.searchText)"
                md-item-text="item"
                md-autoselect="false"
                md-select-on-match="false"
                md-no-cache="true"
                md-require-match="false"
                placeholder="{{ $ctrl.getPlaceholder() | translate }}"
                md-clear-button="false">

                ${defaultAutoCompleteItemTemplate}
            </md-autocomplete>
            ${defaultChipTemplate}

        </md-chips>
    </form>
`;


export default class SearchComponent {
    static config = {
        selector: 'mwSearch',
        bindings: {
            onAutocompleteQuery: '&',
            onChange: '&',
            onClear: '&',
            onAdd: '&',
            onRemove: '&',
            onSearch: '&',
            showIcon: '<',
            clearAlwaysVisible: '<',
            terms: '<',
            isFocused: '<',
            separatorKeys: '<',
            placeholder: '@',
            secondaryPlaceholder: '@',
            chipTemplate: '<',
            itemTemplate: '<'
        },
        controller: SearchComponent,
        template: `
            <div></div>
        `
    };

    private searchText: string = '';

    private chips = {
        isReadOnly: false,
        isRemovable: true,
        separatorKeys: [this.$mdConstant.KEY_CODE.ENTER]
    };

    constructor(private $attrs, private $element, private $mdConstant, private $compile, private $scope) {
        'ngInject';

        this.$attrs = $attrs;
        this.$element = $element;
        this.$compile = $compile;
        this.$scope = $scope;
        this.$mdConstant = $mdConstant;
    }

    $onInit() {
        this.placeholder = this.placeholder || 'Search';
        this.secondaryPlaceholder = this.secondaryPlaceholder || '';
        if (angular.isUndefined(this.terms) || !angular.isArray(this.terms)) this.terms = [];
        if (angular.isUndefined(this.showIcon)) this.showIcon = true;
        if (angular.isUndefined(this.clearAlwaysVisible)) this.clearAlwaysVisible = false;
        if (angular.isUndefined(this.isFocused)) this.isFocused = false;
        if (angular.isDefined(this.separatorKeys)) this.chips.separatorKeys = [...this.chips.separatorKeys, ...this.separatorKeys];

        this.onKeyup = this.onKeyup.bind(this);
        this.$element[0].addEventListener('keyup', this.onKeyup);
    }

    $postLink() {
        let template = defaultTemplate;
        if (angular.isDefined(this.itemTemplate) || angular.isDefined(this.chipTemplate)) {
            if (this.itemTemplate) {
                template = template.replace(defaultAutoCompleteItemTemplate, this.itemTemplate);
            }
            if (this.chipTemplate) {
                template = template.replace(defaultChipTemplate, this.chipTemplate);
            }
        }
        this.$element.empty();
        this.$element.append(this.$compile(template)(this.$scope));
    }

    $onChanges(changes) {
        if (changes.isFocused && changes.isFocused.currentValue) setTimeout( () => { this.focusSearchInput(); }, 0);
    }

    getPlaceholder() {
        if (this.terms.length === 0) {
            return this.placeholder;
        } else {
            if (this.secondaryPlaceholder !== '') return this.secondaryPlaceholder;
            return this.placeholder;
        }
    }

    onAddChip() {
        if (this.termExists(this.terms[this.terms.length - 1])) {
            this.terms.pop();
        } else {
            if (this.$attrs.onAdd) this.onAdd({$event: angular.copy(this.terms)});
            if (this.$attrs.onChange) this.onChange({$event: angular.copy(this.terms)});
        }
    }

    onRemoveChip() {
        if (this.$attrs.onRemove) this.onRemove({$event: angular.copy(this.terms)});
        if (this.$attrs.onChange) this.onChange({$event: angular.copy(this.terms)});
    }

    onKeyup(event) {
        if (event.keyCode === 13) {
            if (this.$attrs.onSearch) this.onSearch({$event: angular.copy(this.terms)});
        }
    }

    deleteAll() {
        this.terms = [];
        this.searchText = '';
        if (this.$attrs.onChange) this.onChange({$event: angular.copy(this.terms)});
        if (this.$attrs.onClear) this.onClear();
    }

    querySearch(searchText: string) {
        if (this.$attrs.onAutocompleteQuery) return this.onAutocompleteQuery({$event: searchText});
        return [];
    }

    focusSearchInput() {
        const autoCompleteElement = this.$element.find('md-autocomplete')[0];
        const inputElement = autoCompleteElement ? autoCompleteElement.getElementsByTagName('input')[0] : null;

        if (inputElement) {
            inputElement.focus();
        }
    }

    termExists(term: string) {
        let find = this.terms.filter( (item: string) => item.toLowerCase() === term.toLowerCase() );
        if (find.length === 1) return false;
        return true;
    }
}



// WEBPACK FOOTER //
// ./src/lib/components/search/search.component.ts